// src/components/settings/ResourceManagementSettings.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, query, onSnapshot, orderBy, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Plus, Edit, Trash2, Truck, Wrench, X } from 'lucide-react';

// Simple Modal Component
const ResourceFormModal = ({ isOpen, onClose, onSave, resourceToEdit }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState('Equipment'); 
    const [status, setStatus] = useState('Available'); 

    useEffect(() => {
        if (resourceToEdit) {
            setName(resourceToEdit.name || '');
            setType(resourceToEdit.type || 'Equipment');
            setStatus(resourceToEdit.status || 'Available');
        } else {
            // Reset form for new resource
            setName('');
            setType('Equipment');
            setStatus('Available');
        }
    }, [resourceToEdit, isOpen]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim()) {
            alert("Resource name cannot be empty.");
            return;
        }
        onSave({ name, type, status });
        onClose(); 
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-in fade-in">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md relative">
                {/* Close Button */}
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                    <X size={20} />
                </button>

                <h3 className="text-lg font-bold mb-4 text-gray-800">{resourceToEdit ? 'Edit Resource' : 'Add New Resource'}</h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="resourceName" className="block text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Name</label>
                        <input
                            type="text"
                            id="resourceName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            required
                            placeholder="e.g. Ford F-150"
                        />
                    </div>
                    <div>
                        <label htmlFor="resourceType" className="block text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Type</label>
                        <select
                            id="resourceType"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg bg-white outline-none"
                        >
                            <option value="Vehicle">Vehicle</option>
                            <option value="Equipment">Equipment</option>
                        </select>
                    </div>
                     <div>
                        <label htmlFor="resourceStatus" className="block text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Status</label>
                        <select
                            id="resourceStatus"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg bg-white outline-none"
                        >
                            <option value="Available">Available</option>
                            <option value="In Repair">In Repair</option>
                            <option value="Unavailable">Unavailable</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">Cancel</button>
                        <button type="submit" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold shadow-sm">Save Resource</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const ResourceManagementSettings = ({ currentUserProfile }) => {
    const [resources, setResources] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [resourceToEdit, setResourceToEdit] = useState(null);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) {
            setIsLoading(false);
            return;
        }
        const orgId = currentUserProfile.organizationId;
        const resourcesCollectionPath = `organizations/${orgId}/resources`;
        const q = query(collection(db, resourcesCollectionPath), orderBy('name'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setResources(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching resources:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [currentUserProfile]);

    const handleSaveResource = async (resourceData) => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
        const resourcesCollectionPath = `organizations/${orgId}/resources`;

        try {
            if (resourceToEdit) {
                // Update existing resource
                const resourceRef = doc(db, resourcesCollectionPath, resourceToEdit.id);
                await updateDoc(resourceRef, resourceData);
            } else {
                // Add new resource
                await addDoc(collection(db, resourcesCollectionPath), resourceData);
            }
        } catch (error) {
            console.error("Error saving resource:", error);
            alert("Failed to save resource.");
        }
    };

    const handleDeleteResource = async (resourceId) => {
        if (!currentUserProfile?.organizationId || !resourceId) return;
        if (!window.confirm("Are you sure you want to delete this resource?")) return;

        const orgId = currentUserProfile.organizationId;
        const resourceRef = doc(db, `organizations/${orgId}/resources`, resourceId);
        try {
            await deleteDoc(resourceRef);
        } catch (error) {
            console.error("Error deleting resource:", error);
            alert("Failed to delete resource.");
        }
    };

    const openAddModal = () => {
        setResourceToEdit(null);
        setIsModalOpen(true);
    };

    const openEditModal = (resource) => {
        setResourceToEdit(resource);
        setIsModalOpen(true);
    };

    return (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Manage Resources</h2>
                <button
                    onClick={openAddModal}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm text-sm"
                >
                    <Plus size={16} /> <span className="hidden sm:inline">Add Resource</span><span className="sm:hidden">Add</span>
                </button>
            </div>

            {isLoading ? (
                <p className="text-gray-500 text-center py-8">Loading resources...</p>
            ) : (
                <div className="space-y-3">
                    {/* REPLACED TABLE WITH MOBILE-FRIENDLY CARDS */}
                    {resources.map((resource) => (
                        <div key={resource.id} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-blue-300 transition-all">
                            
                            {/* Left Side: Info */}
                            <div className="flex items-start gap-4">
                                <div className={`p-2 rounded-full shrink-0 ${resource.type === 'Vehicle' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                                    {resource.type === 'Vehicle' ? <Truck size={20} /> : <Wrench size={20} />}
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900">{resource.name}</h4>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{resource.type}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded border ${
                                            resource.status === 'Available' ? 'bg-green-50 text-green-700 border-green-200' :
                                            resource.status === 'In Repair' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                            'bg-red-50 text-red-700 border-red-200'
                                        }`}>
                                            {resource.status}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Actions (Stacked on Mobile) */}
                            <div className="flex items-center gap-2 w-full sm:w-auto pt-3 sm:pt-0 border-t sm:border-0 border-gray-100">
                                <button onClick={() => openEditModal(resource)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 p-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-blue-50 hover:text-blue-600 font-medium text-sm transition-colors">
                                    <Edit size={16} /> <span className="sm:hidden">Edit</span>
                                </button>
                                <button onClick={() => handleDeleteResource(resource.id)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 p-2 bg-gray-50 text-red-500 rounded-lg hover:bg-red-50 hover:text-red-600 font-medium text-sm transition-colors">
                                    <Trash2 size={16} /> <span className="sm:hidden">Delete</span>
                                </button>
                            </div>
                        </div>
                    ))}
                     {resources.length === 0 && (
                        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-gray-500">
                            No resources added yet.
                        </div>
                    )}
                </div>
            )}

            <ResourceFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveResource}
                resourceToEdit={resourceToEdit}
            />
        </div>
    );
};

export default ResourceManagementSettings;